import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

/**
 * Hero visual: a "signal" composition. Three layers, all SVG, all CSS-animated:
 *   1. Engineered grid backplate
 *   2. Constellation of edge POPs orbiting a central session
 *   3. Live oscilloscope waveform with a chartreuse data packet sweeping it
 * Theme-aware via CSS vars (--brand-1, --brand-2, --signal-live).
 */
export function NetworkAnimation({ className }: { className?: string }) {
  const ref = useRef<SVGSVGElement>(null)
  const [tick, setTick] = useState(0)

  // Drive the latency readout once a second so it feels live without
  // forcing a redraw of the whole composition every frame.
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1100)
    return () => window.clearInterval(id)
  }, [])

  const latency = 18 + ((tick * 7) % 23) // 18–40ms cycling

  return (
    <div className={cn('relative aspect-[5/4] w-full', className)} aria-hidden="true">
      {/* Frame chrome — corner brackets feel like a control-room display */}
      <FrameBrackets />

      {/* Status strip */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 z-20">
        <div className="flex items-center gap-2">
          <span className="live-dot" />
          <span>edge.live</span>
        </div>
        <div className="flex items-center gap-3">
          <span>p50 · {latency}ms</span>
          <span className="hidden sm:inline">jitter · 0.4</span>
        </div>
      </div>

      {/* Footer label */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 z-20">
        <span>session · kx-{(tick % 9999).toString().padStart(4, '0')}</span>
        <span className="hidden sm:inline">300+ pops · global</span>
      </div>

      <svg
        ref={ref}
        viewBox="0 0 500 400"
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Reusable gradient brushes pinned to brand vars */}
          <linearGradient id="brand" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--brand-1))" />
            <stop offset="100%" stopColor="hsl(var(--brand-2))" />
          </linearGradient>
          <linearGradient id="brand-soft" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--brand-1))" stopOpacity="0" />
            <stop offset="50%" stopColor="hsl(var(--brand-1))" stopOpacity="0.7" />
            <stop offset="100%" stopColor="hsl(var(--brand-2))" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="bloom" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(var(--brand-2))" stopOpacity="0.45" />
            <stop offset="60%" stopColor="hsl(var(--brand-2))" stopOpacity="0.05" />
            <stop offset="100%" stopColor="hsl(var(--brand-2))" stopOpacity="0" />
          </radialGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Inner grid */}
        <g opacity="0.45">
          {Array.from({ length: 11 }).map((_, i) => (
            <line
              key={`v${i}`}
              x1={i * 50}
              y1={0}
              x2={i * 50}
              y2={400}
              stroke="hsl(var(--grid-line))"
              strokeWidth={i % 5 === 0 ? 0.6 : 0.3}
            />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <line
              key={`h${i}`}
              x1={0}
              y1={i * 50}
              x2={500}
              y2={i * 50}
              stroke="hsl(var(--grid-line))"
              strokeWidth={i % 4 === 0 ? 0.6 : 0.3}
            />
          ))}
        </g>

        {/* Bloom behind hub */}
        <circle cx="250" cy="170" r="180" fill="url(#bloom)" />

        {/* Three concentric orbits (pulse) */}
        {[ 60, 95, 135 ].map((r, i) => (
          <circle
            key={r}
            cx="250"
            cy="170"
            r={r}
            fill="none"
            stroke="hsl(var(--brand-2))"
            strokeOpacity={0.18 - i * 0.04}
            strokeWidth="1"
            strokeDasharray="2 6"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from={`${i % 2 === 0 ? 0 : 360} 250 170`}
              to={`${i % 2 === 0 ? 360 : 0} 250 170`}
              dur={`${24 + i * 8}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}

        {/* Edge POPs — six nodes evenly distributed on outer orbit */}
        {EDGE_NODES.map((n, i) => (
          <g key={i}>
            {/* Connection line to hub */}
            <line
              x1="250"
              y1="170"
              x2={n.x}
              y2={n.y}
              stroke="url(#brand-soft)"
              strokeWidth="1"
              strokeOpacity="0.5"
            />
            {/* Travelling packet */}
            <circle r="2.4" fill="hsl(var(--brand-1))" filter="url(#glow)">
              <animateMotion
                dur={`${3.2 + (i % 3) * 0.6}s`}
                repeatCount="indefinite"
                path={`M250 170 L${n.x} ${n.y}`}
                begin={`${i * 0.4}s`}
              />
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                dur={`${3.2 + (i % 3) * 0.6}s`}
                repeatCount="indefinite"
                begin={`${i * 0.4}s`}
              />
            </circle>
            {/* Node ring */}
            <circle
              cx={n.x}
              cy={n.y}
              r="9"
              className="fill-background"
              stroke="hsl(var(--brand-2))"
              strokeWidth="1.2"
            />
            <circle
              cx={n.x}
              cy={n.y}
              r="3.2"
              fill="url(#brand)"
              filter="url(#glow)"
            >
              <animate
                attributeName="r"
                values="3;3.8;3"
                dur={`${2.6 + (i * 0.3) % 1.4}s`}
                repeatCount="indefinite"
              />
            </circle>
            {/* Region label */}
            <text
              x={n.labelX}
              y={n.labelY}
              fontFamily="JetBrains Mono, monospace"
              fontSize="9"
              className="fill-muted-foreground"
              opacity="0.85"
              textAnchor={n.anchor}
            >
              {n.label}
            </text>
          </g>
        ))}

        {/* Central hub — session origin */}
        <g transform="translate(250 170)">
          <circle r="22" className="fill-background" />
          <circle r="22" fill="none" stroke="url(#brand)" strokeWidth="1.5" />
          <circle r="14" fill="url(#brand)" opacity="0.18" />
          <circle r="6" fill="url(#brand)" filter="url(#glow)">
            <animate attributeName="r" values="5;7;5" dur="2s" repeatCount="indefinite" />
          </circle>
          <text
            y="44"
            textAnchor="middle"
            fontFamily="JetBrains Mono, monospace"
            fontSize="9"
            className="fill-foreground"
            opacity="0.95"
          >
            SFU · CF
          </text>
        </g>

        {/* Oscilloscope frame */}
        <g transform="translate(40 290)">
          <rect
            x="0"
            y="0"
            width="420"
            height="80"
            className="fill-background"
            opacity="0.4"
            rx="8"
          />
          <rect
            x="0"
            y="0"
            width="420"
            height="80"
            fill="none"
            stroke="hsl(var(--brand-2))"
            strokeOpacity="0.25"
            rx="8"
          />
          {/* Grid hairlines inside scope */}
          {Array.from({ length: 5 }).map((_, i) => (
            <line
              key={i}
              x1="0"
              y1={(i + 1) * 16}
              x2="420"
              y2={(i + 1) * 16}
              stroke="hsl(var(--grid-line))"
              strokeOpacity="0.5"
            />
          ))}
          {/* Center axis */}
          <line
            x1="0"
            y1="40"
            x2="420"
            y2="40"
            stroke="hsl(var(--brand-2))"
            strokeOpacity="0.25"
            strokeDasharray="2 4"
          />
          {/* Scrolling waveform */}
          <g className="wave-flow">
            <path
              d={WAVE_PATH}
              fill="none"
              stroke="url(#brand)"
              strokeWidth="1.6"
              strokeLinecap="round"
              filter="url(#glow)"
            />
            <path
              d={WAVE_PATH_OFFSET}
              fill="none"
              stroke="url(#brand)"
              strokeWidth="1.6"
              strokeLinecap="round"
              filter="url(#glow)"
            />
          </g>
          {/* Live cursor */}
          <line
            x1="380"
            y1="6"
            x2="380"
            y2="74"
            stroke="hsl(var(--signal-live))"
            strokeWidth="1"
            strokeDasharray="2 2"
            opacity="0.6"
          />
          <circle cx="380" cy="40" r="3" fill="hsl(var(--signal-live))">
            <animate attributeName="cy" values="34;46;34" dur="1.4s" repeatCount="indefinite" />
          </circle>
          {/* Scope labels */}
          <text x="8" y="14" fontFamily="JetBrains Mono, monospace" fontSize="9" className="fill-muted-foreground" opacity="0.85">
            audio · 48kHz
          </text>
          <text x="412" y="14" textAnchor="end" fontFamily="JetBrains Mono, monospace" fontSize="9" className="fill-muted-foreground" opacity="0.85">
            +12 dB
          </text>
        </g>
      </svg>
    </div>
  )
}

// Six edge POPs on a circle around the hub. Hand-placed so labels read.
const EDGE_NODES = [
  { x: 100, y: 100, label: 'sfo', anchor: 'end'    as const, labelX: 90,  labelY: 100 },
  { x: 250, y: 60,  label: 'fra', anchor: 'middle' as const, labelX: 250, labelY: 50  },
  { x: 400, y: 100, label: 'sin', anchor: 'start'  as const, labelX: 410, labelY: 100 },
  { x: 410, y: 220, label: 'syd', anchor: 'start'  as const, labelX: 420, labelY: 220 },
  { x: 250, y: 270, label: 'gru', anchor: 'middle' as const, labelX: 250, labelY: 286 },
  { x: 90,  y: 220, label: 'iad', anchor: 'end'    as const, labelX: 80,  labelY: 220 },
]

// Two-cycle wave to keep it seamless when translated -50%.
// y oscillates around 40 (scope center). 840px wide so half-translate = 420.
const WAVE_PATH = (() => {
  const steps = 84
  const w = 840
  let d = ''
  for (let i = 0; i <= steps; i++) {
    const x = (i / steps) * w
    const t = (i / steps) * Math.PI * 6
    const y =
      40 +
      Math.sin(t) * 14 +
      Math.sin(t * 1.7) * 6 +
      Math.sin(t * 2.5) * 2.5
    d += i === 0 ? `M${x.toFixed(1)} ${y.toFixed(1)}` : ` L${x.toFixed(1)} ${y.toFixed(1)}`
  }
  return d
})()

const WAVE_PATH_OFFSET = (() => {
  const steps = 84
  const w = 840
  let d = ''
  for (let i = 0; i <= steps; i++) {
    const x = (i / steps) * w
    const t = (i / steps) * Math.PI * 6 + Math.PI / 2
    const y = 40 + Math.sin(t) * 8 + Math.sin(t * 2.1) * 3
    d += i === 0 ? `M${x.toFixed(1)} ${y.toFixed(1)}` : ` L${x.toFixed(1)} ${y.toFixed(1)}`
  }
  return d
})()

// Decorative corner brackets — gives the visual a control-room frame
function FrameBrackets() {
  const cls = 'absolute h-3 w-3 border-[hsl(var(--brand-2))] opacity-60'
  return (
    <>
      <span className={cn(cls, 'top-2 left-2 border-t border-l')} />
      <span className={cn(cls, 'top-2 right-2 border-t border-r')} />
      <span className={cn(cls, 'bottom-2 left-2 border-b border-l')} />
      <span className={cn(cls, 'bottom-2 right-2 border-b border-r')} />
    </>
  )
}
